import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const dot = (c, pulse) => `<span style="position:relative;display:flex;height:16px;width:16px;align-items:center;justify-content:center">${pulse ? `<span style="position:absolute;inset:0;border-radius:9999px;background:${c};animation:rs-ping 1.8s cubic-bezier(0,0,0.2,1) infinite"></span>` : ""}<span style="position:relative;height:13px;width:13px;border-radius:9999px;background:${c};border:2px solid #0a0f1a;box-shadow:0 0 0 1px ${c}"></span></span>`;
const ok = (p) => p && typeof p.lat === "number" && typeof p.lng === "number";

// Vanilla Leaflet trip map: pickup, drop-off, travelled path and the driver's live position.
export default function TripMap({ pickup, dropoff, path = [], driver }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const fitted = useRef(false);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return undefined;
    const map = L.map(elRef.current, { scrollWheelZoom: false });
    map.setView([42.36, -71.06], 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      subdomains: "abc", className: "rs-dark-tiles", maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 120);
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; fitted.current = false; };
  }, []);

  useEffect(() => {
    const map = mapRef.current, group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const pts = [];
    const line = path.filter(ok).map((p) => [p.lat, p.lng]);
    if (line.length > 1) { L.polyline(line, { color: "#3b82f6", weight: 4, opacity: 0.8 }).addTo(group); pts.push(...line); }
    [[pickup, "#22C55E", "Pickup"], [dropoff, "#EF4444", "Drop-off"]].forEach(([p, c, label]) => {
      if (!ok(p)) return;
      L.marker([p.lat, p.lng], { icon: L.divIcon({ className: "", html: dot(c), iconSize: [16, 16], iconAnchor: [8, 8] }) })
        .bindPopup(`<b>${label}</b><br/>${p.label || ""}`).addTo(group);
      pts.push([p.lat, p.lng]);
    });
    if (ok(driver)) {
      L.marker([driver.lat, driver.lng], { icon: L.divIcon({ className: "", html: dot("#60A5FA", true), iconSize: [16, 16], iconAnchor: [8, 8] }), zIndexOffset: 1000 })
        .bindPopup(`<b>${driver.label || "Driver"}</b><br/>Live position`).addTo(group);
      pts.push([driver.lat, driver.lng]);
    }
    if (!fitted.current && pts.length) {
      if (pts.length === 1) map.setView(pts[0], 13); else map.fitBounds(pts, { padding: [40, 40] });
      fitted.current = true;
    }
  }, [pickup, dropoff, path, driver]);

  return <div ref={elRef} className="relative z-0 h-[380px] w-full overflow-hidden rounded-lg border border-slate-800" data-testid="trip-map" />;
}
